import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@eventbrite/eds-button';
import { gettext } from '@eventbrite/i18n';
import { Icon } from '@eventbrite/eds-icon';
import { Checkbox } from '@eventbrite/eds-checkbox';
import { COLUMNS_PROP_TYPE } from '@eventbrite/eds-data-table-list-item';

import './dataTableHeader.scss';

import { ChevronUpChunky } from '@eventbrite/eds-iconography';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';

const DataTableHeaderCheckbox = ({
    ariaLabel,
    isSelectable,
    isSelected,
    onSelect,
}) => {
    if (!isSelectable) {
        return null;
    }

    return (
        <th className="eds-data-table-header__column">
            <Checkbox
                id="data-table-header-checkbox"
                value={!!isSelected}
                onChange={onSelect}
                ariaLabel={ariaLabel}
            />
        </th>
    );
};

const DataTableHeaderColumn = ({
    ariaLabel = '',
    fieldLabel,
    icon,
    onSortChange,
    isSortable,
}) => (
    <th
        className="eds-data-table-header__column"
        data-spec="data-table-header-column"
    >
        <Button
            aria-label={ariaLabel.toString()}
            style="none"
            onClick={onSortChange}
            disabled={!isSortable}
            __containerClassName="eds-data-table-header__column-button"
        >
            <span className="eds-text-bm">{fieldLabel}</span>
            <span className="eds-data-table-header__column__icon">{icon}</span>
        </Button>
    </th>
);

export default class DataTableHeader extends React.Component {
    static propTypes = {
        /**
         * Columns must be an array of objects made up of strings and booleans.
         */
        columns: COLUMNS_PROP_TYPE.isRequired,
        /**
         * If isSelectable is true, the DataTableHeader will render with a checkbox to selectAll for batch actions provided by the DynamicDataTable. If isSelectable is false, the DataTableHeader will render without a checkbox.
         */
        isSelectable: PropTypes.bool,
        /**
         * If isSelected is true, the DataTableHeader will render with a checkbox that is checked. If isSelected is false, the DataTableHeader will render with a checkbox that is not checked.
         */
        isSelected: PropTypes.bool,
        /**
         * The DataTableHeader will always be sorted by one of its columns specified by the sortColumn prop.
         */
        sortColumn: PropTypes.string,
        /**
         * Because the DataTableHeader will always be sorted by one of its columns, it will also always have a sort direction, specified by the sortDescending prop.
         */
        sortDescending: PropTypes.bool,
        /**
         * {function} onSelect is passed down from DataTable and handles the select/deselect of the checkbox.
         */
        onSelect: PropTypes.func,
        /**
         * {function} onSortChange handles the data sorting when the sortColumn or sortDirection changes
         */
        onSortChange: PropTypes.func,
    };

    static defaultProps = {
        isSelectable: false,
        isSelected: false,
    };

    _handleSelect(isChecked) {
        if (this.props.onSelect) {
            this.props.onSelect(isChecked);
        }
    }

    _handleSortChange(columnFieldName) {
        if (this.props.onSortChange) {
            this.props.onSortChange(columnFieldName);
        }
    }

    _getSortedIcon(column) {
        let sortedIcon;
        const { sortColumn, sortDescending } = this.props;

        if (sortColumn === column) {
            let arrowType = <ChevronUpChunky />;

            if (sortDescending) {
                arrowType = <ChevronDownChunky />;
            }

            sortedIcon = (
                <Icon type={arrowType} size="xsmall" color="grey-600" />
            );
        }

        return sortedIcon;
    }

    _getColumns(columns) {
        return columns.map(
            ({ ariaLabel, fieldName, fieldLabel, isSortable }) => {
                let column = (
                    <DataTableHeaderColumn
                        key={fieldName}
                        fieldLabel={fieldLabel}
                        ariaLabel={ariaLabel}
                    />
                );

                if (isSortable) {
                    const icon = this._getSortedIcon(fieldName);

                    column = (
                        <DataTableHeaderColumn
                            icon={icon}
                            onSortChange={this._handleSortChange.bind(
                                this,
                                fieldName,
                            )}
                            fieldLabel={fieldLabel}
                            ariaLabel={ariaLabel}
                            key={fieldName}
                            isSortable={isSortable}
                        />
                    );
                }

                return column;
            },
        );
    }

    _getFirstColumnAriaLabel = (columns) => {
        if (columns[0]?.ariaLabel) {
            return gettext('Select all %(dataTableHeaderDescription)s', {
                dataTableHeaderDescription: columns[0].ariaLabel.toString(),
            });
        }

        return '';
    };

    render() {
        const { columns, isSelectable, isSelected } = this.props;

        const headerColumns = this._getColumns(columns);
        const ariaLabelForHeaderCheckbox =
            this._getFirstColumnAriaLabel(columns);

        return (
            <thead>
                <tr
                    data-spec="data-table-header"
                    className="eds-data-table-header"
                >
                    <DataTableHeaderCheckbox
                        isSelectable={isSelectable}
                        isSelected={isSelected}
                        onSelect={this._handleSelect.bind(this)}
                        ariaLabel={ariaLabelForHeaderCheckbox}
                    />
                    {headerColumns}
                </tr>
            </thead>
        );
    }
}
